type ZafClient = {
  get: (path: string | string[]) => Promise<Record<string, unknown>>;
  invoke: (method: string, ...args: unknown[]) => Promise<unknown>;
  on: (event: string, callback: (...args: unknown[]) => void) => void;
  off: (event: string, callback: (...args: unknown[]) => void) => void;
};

declare global {
  interface Window {
    ZAFClient?: {
      init: () => ZafClient;
    };
  }
}

let client: ZafClient | null = null;

export function getZafClient(): ZafClient | null {
  if (client) {
    return client;
  }
  if (typeof window !== 'undefined' && window.ZAFClient) {
    client = window.ZAFClient.init();
    return client;
  }
  return null;
}

export const REQUESTER_UNAVAILABLE_ERROR = 'ZAF_REQUESTER_UNAVAILABLE';

export type TicketRequester = {
  email: string | null;
  phone: string | null;
};

type RequesterIdentity = { type?: string; value?: string; primary?: boolean };

const readString = (value: unknown): string | null => {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
};

// Zendesk stores phone numbers as requester identities (type "phone_number"), not as a plain field.
const readPhoneIdentity = (identities: unknown): string | null => {
  if (!Array.isArray(identities)) {
    return null;
  }
  const phones = identities
    .filter((identity): identity is RequesterIdentity => typeof identity === 'object' && identity !== null)
    .filter(identity => identity.type === 'phone_number' && readString(identity.value) !== null);
  const primary = phones.find(identity => identity.primary) ?? phones.at(0);
  return primary ? readString(primary.value) : null;
};

export async function getTicketRequester(): Promise<TicketRequester> {
  const mockEmail = readString(import.meta.env.VITE_MOCK_REQUESTER_EMAIL);
  const mockPhone = readString(import.meta.env.VITE_MOCK_REQUESTER_PHONE);
  if (mockEmail || mockPhone) {
    return { email: mockEmail, phone: mockPhone };
  }

  const zaf = getZafClient();
  if (!zaf) {
    return { email: null, phone: null };
  }

  try {
    const data = await zaf.get(['ticket.requester.email', 'ticket.requester.identities']);
    return {
      email: readString(data['ticket.requester.email']),
      phone: readPhoneIdentity(data['ticket.requester.identities']),
    };
  } catch {
    throw new Error(REQUESTER_UNAVAILABLE_ERROR);
  }
}

export async function getAgentLocale(): Promise<string | null> {
  const zaf = getZafClient();
  if (!zaf) {
    return null;
  }
  try {
    const data = await zaf.get('currentUser.locale');
    return readString(data['currentUser.locale']);
  } catch {
    return null;
  }
}

export async function resizeSidebar(height = '520px'): Promise<void> {
  const zaf = getZafClient();
  if (!zaf) {
    return;
  }
  try {
    await zaf.invoke('resize', { width: '100%', height });
  } catch {
    // ignore outside Zendesk
  }
}
